import { cn } from '@/lib/cn'
import type { ReactNode } from 'react'

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-ink-faint">
      <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      {label}
    </span>
  )
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-40 items-center justify-center p-8">
      <Spinner label={label} />
    </div>
  )
}

/** Skeleton rows while a list loads. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2.5" aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-16 animate-pulse rounded-[var(--radius-card)] bg-surface-sunken"
        />
      ))}
    </div>
  )
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string
  body?: ReactNode
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-dashed border-line-strong bg-surface px-6 py-14 text-center">
      {icon ? (
        <div className="grid size-11 place-items-center rounded-full bg-surface-sunken text-ink-faint">
          {icon}
        </div>
      ) : null}
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        {body ? (
          <p className="mx-auto mt-1 max-w-sm text-pretty text-sm text-ink-faint">{body}</p>
        ) : null}
      </div>
      {action}
    </div>
  )
}

export function ErrorNote({
  title = 'Something went wrong',
  body,
  onRetry,
  className,
}: {
  title?: string
  body?: ReactNode
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)]',
        'border border-critical/30 bg-critical-soft px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-critical">{title}</p>
        {body ? <p className="mt-0.5 text-pretty text-xs text-ink-soft">{body}</p> : null}
      </div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="h-8 shrink-0 rounded-[var(--radius-field)] border border-critical/40 px-3 text-xs font-medium text-critical hover:bg-critical/10"
        >
          Try again
        </button>
      ) : null}
    </div>
  )
}

/** Inline form error banner. */
export function FormError({ children }: { children?: ReactNode }) {
  if (!children) return null
  return (
    <p
      role="alert"
      className="rounded-[var(--radius-field)] border border-critical/30 bg-critical-soft px-3 py-2 text-xs text-pretty text-critical"
    >
      {children}
    </p>
  )
}


